import { action, computed, makeObservable } from 'mobx'
import { SearchableUser } from 'iam/models/SearchableUser'
import SearchableUserStore from 'iam/SearchableUserStore'
import { GetCurrentMember } from 'iam/SessionStore'
import WorkspaceMembershipStore from 'iam/WorkspaceMembershipStore'
import { FilterStorage } from 'infra/FiltersViewModel'
import link from 'infra/link'
import { Storage } from 'infra/storage'
import { Id } from 'types/definitions'
import { ClientUserSearchViewModel } from 'client-user-management/ClientUserSearchViewModel'
import { ClientInvitationViewModel } from 'client-user-management/views/ClientInvitationDialog/ClientInvitationViewModel'
import MemberStore from 'iam/MemberStore'
import { CustomerStore } from 'iam/CustomerStore'
import { AlertFactory } from 'alerts/Alert'

export interface ClientUserColumn {
  id: string
  header: string
  modifyVisibility: boolean
  sortable: boolean
  visible: boolean
}

const ClientUserManagementColumns: Array<ClientUserColumn> = [
  {
    id: 'name',
    header: 'Name',
    modifyVisibility: true,
    visible: true,
    sortable: false,
  },
  {
    id: 'email',
    header: 'Email',
    modifyVisibility: true,
    visible: true,
    sortable: false,
  },
  {
    id: 'lastLogin',
    header: 'Last Login',
    modifyVisibility: true,
    visible: true,
    sortable: false,
  },
  {
    id: 'dateCreated',
    header: 'Date Created',
    modifyVisibility: true,
    visible: true,
    sortable: false,
  },
]

export class ClientUserManagementViewModel {
  columns = ClientUserManagementColumns

  userSearch = new ClientUserSearchViewModel(
    this.searchableUserStore,
    this.getCurrentMember,
    (ns) => new FilterStorage(this.localStorage, ns),
  )

  clientInvitationVM = new ClientInvitationViewModel(
    this.getCurrentMember,
    this.memberStore,
    this.customerStore,
    this.newAlert,
  )

  constructor(
    private getCurrentMember: GetCurrentMember,
    private localStorage: Storage,
    private searchableUserStore: SearchableUserStore,
    private workspaceMembershipStore: WorkspaceMembershipStore,
    private memberStore: MemberStore,
    private customerStore: CustomerStore,
    private newAlert: AlertFactory,
  ) {
    makeObservable(this)
  }

  activate() {
    this.userSearch.activate()
  }

  deactivate() {
    this.userSearch.deactivate()
  }

  @computed
  get isSearchFilterApplied() {
    const isSearching = this.userSearch.searchText.value !== ''
    const haveFiltersApplied = this.userSearch.filters.appliedFilterCount > 0

    return isSearching || haveFiltersApplied
  }

  @computed
  get shouldShowEmptyState() {
    return this.userSearch.scrollProvider.isDone && this.users.length === 0
  }

  @computed
  get canAccessClientUserManagement() {
    return this.getCurrentMember().hasPermission('ADMIN_CLIENT_USER_LIST')
  }

  @computed
  get canInviteClients() {
    const currentMember = this.getCurrentMember()
    const hasAdminUserCreate = currentMember.hasPermission('ADMIN_USER_CREATE')

    // Users with ADMIN_USER_CREATE can always invite
    if (hasAdminUserCreate) {
      return true
    }

    // Users with CUSTOMER_CREATE can only invite if workspace supports external customers
    const hasCustomerCreate = currentMember.hasPermission('CUSTOMER_CREATE')
    const workspace = currentMember.workspace
    const externalCustomersConfig = workspace.config?.external_customers_config
    const workspaceSupportsCustomers =
      externalCustomersConfig?.enabled &&
      externalCustomersConfig?.allow_requesting_magic_links

    return hasCustomerCreate && workspaceSupportsCustomers
  }

  @computed
  get users() {
    return this.userSearch.scrollProvider.items
  }

  /**
   * Clears any search filters.
   */
  @action.bound
  clearFilters() {
    this.userSearch.searchText.clear()
    this.userSearch.filters.clearExplicitFilters()
  }

  getUserDetailsLinkForUser(user: SearchableUser) {
    return link.clientUserDetails(Id.assert(user.publicId))
  }
}
